import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Segment, Sidebar } from 'semantic-ui-react';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment';
import _ from 'lodash';
import { isMobileOnly } from 'mobile-device-detect';

import { Addons, HotelAddonAvailability, HotelAddonInventory, Hotels } from '../../api/services/next';
import { isWeekendByLocale } from '../../components/shared/utils';
import { onError } from '../../components/shared/messages';
import { PermissionsActions, UserHasPermission } from '../../components/shared/permissionsActions';
import { useLoader } from '../../hooks/useLoader';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useTextResources } from '../../language';

import AddonReservationsDetails from './AddonReservationsDetails';
import EditHotelAddonAvailabilityPopup from './EditHotelAddonAvailability';
import HotelAddonsToolbar from './toolbar';
import './style.less';
import '../TapeChart/style.less';
import { AvailabilityAll, AvailabilityMax, AvailabilityMid, AvailabilityMin, initialViewSettings } from './constants.js';

const hasPermission = UserHasPermission(PermissionsActions.ViewHotelAddons);

const HotelAddonsTapeChart = () => {
    const text = useTextResources();
    const loader = useLoader();
    const calendarRef = useRef(null);

    // View settings state
    const [viewSettings, setViewSettings] = useLocalStorageState('HotelAddonsAvailabilityViewSettings', initialViewSettings);
    const [defaultView, setDefaultView] = useState(viewSettings.defaultView || 'resourceTimelineSlidingMonth');

    // Filter states
    const [selectedHotelIds, setSelectedHotelIds] = useState([]);
    const [selectedChainAddonIds, setSelectedChainAddonIds] = useState([]);

    // Data states
    const [hotels, setHotels] = useState([]);
    const [addons, setAddons] = useState([]);
    const [hotelAddonInventory, setHotelAddonInventory] = useState([]);
    const [availability, setAvailability] = useState([]);
    const [dateInfo, setDateInfo] = useState(null);

    // Modal/Sidebar states
    const [editAddonAvailabilityOpen, setEditAddonAvailabilityOpen] = useState(false);
    const [addonReservationsSidebarOpen, setAddonReservationsSidebarOpen] = useState(false);
    const [selectionInfo, setSelectionInfo] = useState(null);

    // Calendar view configurations
    const defaultMonthLabelFormat = useMemo(() => [{ weekday: 'short' }, { day: '2-digit' }], []);
    const defaultWeekLabelFormat = useMemo(() => [{ weekday: 'short' }, { day: '2-digit' }], []);
    const defaultDuration = useMemo(() => ({ hours: 24 }), []);

    const views = useMemo(() => ({
        resourceTimelineDay: {
            slotLabelFormat: [
                { day: '2-digit', weekday: 'long' },
                { hour: '2-digit', hour12: false, minute: '2-digit' },
            ],
            slotDuration: { hours: 1 },
            slotLabelInterval: { hours: 1 },
        },
        resourceTimelineWeek: {
            slotLabelFormat: defaultWeekLabelFormat,
            slotDuration: defaultDuration,
            slotLabelInterval: defaultDuration,
        },
        resourceTimelineBiWeek: {
            slotLabelFormat: defaultWeekLabelFormat,
            type: 'resourceTimeline',
            duration: { days: 14 },
            slotDuration: defaultDuration,
            slotLabelInterval: defaultDuration,
        },
        resourceTimelineMonth: {
            slotLabelFormat: defaultMonthLabelFormat,
            slotDuration: defaultDuration,
            slotLabelInterval: defaultDuration,
        },
        resourceTimelineSlidingMonth: {
            slotLabelFormat: defaultMonthLabelFormat,
            type: 'resourceTimeline',
            dayCount: 30,
            duration: { days: 30 },
            slotDuration: defaultDuration,
            slotLabelInterval: defaultDuration,
        },
    }), [defaultDuration, defaultMonthLabelFormat, defaultWeekLabelFormat]);

    // Build resources from hotelAddonInventory
    const resources = useMemo(() => {
        if (!hotels.length || !addons.length || !hotelAddonInventory.length) {
            return [];
        }

        return _(hotelAddonInventory)
            .orderBy('ViewOrder', 'asc')
            .map(ai => {
                const hotel = hotels.find(h => h.Id === ai.HotelId);
                const addon = addons.find(a => a.Id === ai.ChainAddonId);
                if (!hotel || !addon) return null;

                return {
                    id: `${hotel.Id}-${addon.Id}`,
                    title: addon.Name,
                    group: hotel.Name,
                    hotelId: hotel.Id,
                    order: ai.ViewOrder,
                    extendedProps: { addon, hotel, addonInventory: ai },
                };
            })
            .filter(Boolean)
            .value();
    }, [hotels, addons, hotelAddonInventory]);

    // Transform availability data to events
    const eventDataTransform = useCallback((avail) => {
        const { showAvailable, showSold, showInventory } = viewSettings;

        const title = [];
        if (!showAvailable && !showSold && !showInventory) {
            title.push(avail.AvailableInventory);
            title.push(avail.Sold);
            title.push(avail.GrossInventory);
        } else {
            if (showAvailable) title.push(avail.AvailableInventory);
            if (showSold) title.push(avail.Sold);
            if (showInventory) title.push(avail.GrossInventory);
        }

        const classNames = [];
        if (!avail.IsAvailableForSale) {
            classNames.push('unavailable-for-sale');
        } else if (!avail.AvailableInventory) {
            classNames.push('sold-out');
        } else if (avail.AvailableInventory < 0) {
            classNames.push('exception');
        } else if (viewSettings.selectedAvailabilityView !== AvailabilityAll) {
            const ratio = avail.AvailableInventory / avail.GrossInventory;
            if (ratio >= viewSettings.showAvailabilityRatioFrom &&
                ratio <= viewSettings.showAvailabilityRatioTo) {
                switch (viewSettings.selectedAvailabilityView) {
                    case AvailabilityMin:
                        classNames.push('availability-view-min');
                        break;
                    case AvailabilityMid:
                        classNames.push('availability-view-mid');
                        break;
                    case AvailabilityMax:
                        classNames.push('availability-view-max');
                        break;
                }
            }
        }

        const hotel = hotels.find(h => h.Id === avail.HotelId);
        const addonInv = hotelAddonInventory
            .find(hai => hai.HotelId === avail.HotelId && hai.ChainAddonId === avail.ChainAddonId)
            ?.RoomTypeAddonInventoryMappings;

        return {
            id: `${avail.HotelId}-${avail.Id}`,
            start: moment(avail.Date).startOf('day').toISOString(),
            end: moment(avail.Date).endOf('day').toISOString(),
            title: title.join('/'),
            resourceId: `${avail.HotelId}-${avail.ChainAddonId}`,
            extendedProps: { availability: avail, hotel, addonInventory: addonInv },
            classNames,
        };
    }, [hotels, hotelAddonInventory, viewSettings]);

    // Event sources for FullCalendar
    const eventSources = useMemo(() => [
        {
            id: 'availability',
            className: 'availability-event',
            eventDataTransform,
            events: availability,
        },
    ], [eventDataTransform, availability]);

    // Initial data load - Hotels
    useEffect(() => {
        (async () => {
            try {
                loader(true);
                const results = await Hotels.GetHotels({
                    IsActive: true,
                    IncludeParameters: true,
                    IncludeOnlyHotelsWithUserPermissions: true,
                    IncludeHotelFeatures: true,
                    IncludeLocation: true,
                });
                setHotels(results);
            } catch (e) {
                onError(e);
            } finally {
                loader(false);
            }
        })();
    }, [loader]);

    // Fetch addons when date range changes
    useEffect(() => {
        if (!dateInfo) return;

        (async () => {
            try {
                const results = await Addons.GetHotelChainAddons({
                    IsActive: true,
                    FromDate: dateInfo.startStr,
                    ToDate: dateInfo.endStr,
                });
                setAddons(results);
            } catch (e) {
                onError(e);
            }
        })();
    }, [dateInfo]);

    // Fetch hotel addon inventory when filters change
    useEffect(() => {
        (async () => {
            try {
                const results = await HotelAddonInventory.GetHotelAddonInventory({
                    isManaged: true,
                    IsActive: true,
                    includes: ['RoomTypeAddonInventoryMappings'],
                    HotelIds: selectedHotelIds.length ? selectedHotelIds : undefined,
                    ChainAddonIds: selectedChainAddonIds.length ? selectedChainAddonIds : undefined,
                });
                setHotelAddonInventory(results);
            } catch (e) {
                onError(e);
            }
        })();
    }, [selectedHotelIds, selectedChainAddonIds]);

    // Fetch availability when date range or settings change
    useEffect(() => {
        if (!dateInfo || !hotels.length) return;

        (async () => {
            try {
                loader(true);
                const results = await HotelAddonAvailability.GetHotelAddonAvailability({
                    HotelIds: selectedHotelIds.length ? selectedHotelIds : hotels.map(h => h.Id),
                    ChainAddonIds: selectedChainAddonIds.length ? selectedChainAddonIds : undefined,
                    ActiveAddonInventory: true,
                    IsManagedAddonInventory: true,
                    FromDate: dateInfo.startStr,
                    ToDate: dateInfo.endStr,
                    showSoldAddons: viewSettings.showSoldAddons,
                    showGrossInventoryDiff: viewSettings.showGrossInventoryDiff,
                });
                setAvailability(results || []);
            } catch (e) {
                onError(e);
            } finally {
                loader(false);
            }
        })();
    }, [dateInfo, hotels, selectedHotelIds, selectedChainAddonIds, viewSettings.showSoldAddons, viewSettings.showGrossInventoryDiff, loader]);

    // Toolbar handlers
    const handlePrev = useCallback(() => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) calendarApi.prev();
    }, []);

    const handleNext = useCallback(() => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) calendarApi.next();
    }, []);

    const handleSetDate = useCallback((date) => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) calendarApi.gotoDate(date);
    }, []);

    const handleChangeView = useCallback((view) => {
        setDefaultView(view);
        setViewSettings(prev => ({ ...prev, defaultView: view }));
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) calendarApi.changeView(view);
    }, [setViewSettings]);

    const handleToggleSetting = useCallback((setting) => {
        setViewSettings(prev => ({
            ...prev,
            [setting]: !prev[setting],
        }));
    }, [setViewSettings]);

    const handleChangeAvailabilityView = useCallback((value, checked) => {
        const _value = checked ? value : AvailabilityAll;
        const splittedValue = _value.split('-');
        setViewSettings(prev => ({
            ...prev,
            showAvailabilityRatioFrom: Number(splittedValue[0]),
            showAvailabilityRatioTo: Number(splittedValue[1]),
            selectedAvailabilityView: _value,
        }));
    }, [setViewSettings]);

    // Event handlers
    const handleEventClick = useCallback((info) => {
        const event = info.event;
        if (!event) return;

        const { hotel, availability: avail, addonInventory } = event.extendedProps;
        const selectInfo = {
            hotelId: hotel.Id,
            chainAddonId: avail.ChainAddonId,
            date: moment(avail.Date).toDate(),
            roomTypeIds: addonInventory?.map(item => item.RoomTypeId) || [],
        };

        setSelectionInfo(selectInfo);
        _.defer(() => setAddonReservationsSidebarOpen(true));
    }, []);

    const handleSelect = useCallback((selectInfo) => {
        if (!hasPermission) return;

        // Adjust end date for non-day views
        if (selectInfo.view.type !== 'resourceTimelineDay') {
            selectInfo = {
                ...selectInfo,
                end: moment(selectInfo.end).subtract(1, 'day').toDate(),
                endStr: moment(selectInfo.end).subtract(1, 'day').toISOString(),
            };
        }

        const { hotel, addon } = selectInfo.resource.extendedProps;
        const info = {
            ...selectInfo,
            hotelId: hotel.Id,
            selectedChainAddonIds: [addon.Id],
            start: selectInfo.start,
            end: selectInfo.end,
        };

        setSelectionInfo(info);
        _.defer(() => setEditAddonAvailabilityOpen(true));
    }, []);

    const selectAllow = useCallback((info) => {
        if (!hasPermission) return false;
        return moment(info.start).isSameOrAfter(moment().startOf('day'));
    }, []);

    // Sidebar/Modal close handlers
    const handleCloseSidebar = useCallback((e) => {
        if (e == null || e.target?.classList?.contains('pusher')) {
            setAddonReservationsSidebarOpen(false);
            setSelectionInfo(null);
        }
    }, []);

    const handleCloseEditAddonAvailability = useCallback((refetch) => {
        if (refetch && calendarRef.current) {
            // Trigger a refetch of availability data
            const calendarApi = calendarRef.current.getApi();
            if (calendarApi) {
                // Re-fetch by updating the dateInfo slightly to trigger useEffect
                setDateInfo(prev => prev ? { ...prev } : null);
            }
        }
        setSelectionInfo(null);
        setEditAddonAvailabilityOpen(false);
    }, []);

    return (
        <React.Fragment>
            <Sidebar.Pushable className="tapechart hotel-addons-availability">
                <Sidebar
                    visible={addonReservationsSidebarOpen}
                    width="wide"
                    animation="overlay"
                    onHide={handleCloseSidebar}
                    as={Segment}
                    direction="right"
                    className={`reservation ${isMobileOnly ? 'mobile' : ''}`}
                >
                    {addonReservationsSidebarOpen && selectionInfo && (
                        <AddonReservationsDetails
                            date={selectionInfo.date}
                            chainAddonId={selectionInfo.chainAddonId}
                            hotelId={selectionInfo.hotelId}
                            hideSidebar={handleCloseSidebar}
                            roomTypeIds={selectionInfo.roomTypeIds}
                        />
                    )}
                </Sidebar>
                <Sidebar.Pusher dimmed={addonReservationsSidebarOpen}>
                    <div className="calendar">
                        <HotelAddonsToolbar
                            title={dateInfo?.view?.title}
                            defaultView={defaultView}
                            onPrev={handlePrev}
                            onNext={handleNext}
                            onChangeView={handleChangeView}
                            onSetDate={handleSetDate}
                            viewSettings={viewSettings}
                            onToggleSetting={handleToggleSetting}
                            onChangeAvailabilityView={handleChangeAvailabilityView}
                            hotels={hotels}
                            addons={addons}
                            setSelectedHotelIds={setSelectedHotelIds}
                            setSelectedChainAddonIds={setSelectedChainAddonIds}
                        />
                        <FullCalendar
                            ref={calendarRef}
                            schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
                            plugins={useMemo(() => [resourceTimelinePlugin, interactionPlugin], [])}
                            initialView={defaultView}
                            views={views}
                            height="100%"
                            headerToolbar={false}
                            nowIndicator={false}
                            locale={window.user?.UserSettings?.[0]?.Language?.Code}
                            resourceAreaWidth={isMobileOnly ? 120 : 240}
                            slotWidth={60}
                            displayEventTime={false}
                            selectable={true}
                            editable={false}
                            selectMinDistance={10}
                            selectAllow={selectAllow}
                            datesSet={setDateInfo}

                            /* Events */
                            eventSources={eventSources}
                            eventClick={handleEventClick}

                            /* Selection */
                            select={handleSelect}

                            /* Resources */
                            resources={resources}
                            resourceGroupField="group"
                            resourceOrder="group,order"
                            resourcesInitiallyExpanded={false}
                            resourceAreaHeaderContent={text('Addons')}
                            
                            slotLaneClassNames={useCallback(({ date }) => {
                                const locale = window.user?.UserSettings?.[0]?.Language?.Code || 'en';
                                return isWeekendByLocale(locale, date) ? ['fc-weekend'] : [];
                            }, [])}
                            
                            slotLabelClassNames={useCallback(({ date }) => {
                                const locale = window.user?.UserSettings?.[0]?.Language?.Code || 'en';
                                return isWeekendByLocale(locale, date) ? ['fc-weekend'] : [];
                            }, [])}
                        />
                    </div>
                </Sidebar.Pusher>
            </Sidebar.Pushable>

            {hasPermission && editAddonAvailabilityOpen && (
                <EditHotelAddonAvailabilityPopup
                    open={editAddonAvailabilityOpen}
                    hotelId={selectionInfo?.hotelId}
                    onClose={handleCloseEditAddonAvailability}
                    fromDate={selectionInfo?.start}
                    toDate={selectionInfo?.end}
                    selectedChainAddonIds={selectionInfo?.selectedChainAddonIds}
                />
            )}
        </React.Fragment>
    );
};

export default HotelAddonsTapeChart;
